import { UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  hasPlatformPartnerAccess,
  Partner,
  useCurrentUserProfile,
  useGroupRules,
  useSelectedPartner,
} from 'src/graphql';
import { FRIENDLY_PARTNER_ID_FIELD } from 'src/routes/constants';
import { useGlobalStore } from 'src/store';
import { useRouteParams } from 'src/utils/routing';

const ref = { isReady: false };

export const useSyncPartnerUrlAccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCurrentUserProfile();
  const { selectedPartner, partners, loading } = useSelectedPartner();
  const { groupRules } = useGroupRules();
  const [hasBeenReady, setHasBeenReady] = useState(false);
  const setSelectedPartnerId = useGlobalStore.use.setSelectedPartnerId();
  const setMultiTenancyMode = useGlobalStore.use.setMultiTenancyMode();

  const { friendlyPartnerId: rawIdFromUrl } = useRouteParams(UrlParams.friendlyPartnerId);
  const idFromUrl = rawIdFromUrl ?? '';
  const idFromStore = getPartnerFriendlyIdentifier(selectedPartner) ?? '';

  // We take the count to use it as a useEffect dependency without useMemo wrappers because
  // Apollo often repopulates its cache incrementally and we may
  // receive one partner object now, the rest a few ms later. But it does that
  // re-using object references, that means useEffect will not re-calculate when needed
  const partnersCount = Object.values(partners ?? {}).length;

  const handlePartnerSelect = useCallback(
    (partnerId: number) => {
      setSelectedPartnerId(partnerId);

      navigate(
        buildPartnerUrl({
          existingIdFromUrl: idFromUrl,
          newPartner: partners?.[partnerId] ?? null,
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        }),
        { replace: true },
      );
    },
    [
      setSelectedPartnerId,
      navigate,
      idFromUrl,
      partners,
      location.pathname,
      location.search,
      location.hash,
    ],
  );

  // multi-tenancy mode access handler
  useEffect(() => {
    if (loading || !user || !partners) {
      return;
    }

    const multiTenant = partnersCount > 1;

    setMultiTenancyMode(multiTenant);

    // no slug in URL - it is multi-tenancy mode
    if (!idFromUrl) {
      if (!multiTenant) {
        // single‑tenant: force the user’s only partner
        handlePartnerSelect(user.partnerId);
      } else {
        // multi‑tenant: clear selection
        setSelectedPartnerId(null);
      }
    }
    // otherwise, leave routing to isolation effect-handler
  }, [
    loading,
    user,
    partners,
    partnersCount,
    idFromUrl,
    setMultiTenancyMode,
    handlePartnerSelect,
    setSelectedPartnerId,
  ]);

  // isolation handler, whenever the URL slug changes, sync it → store
  useEffect(() => {
    if (loading || !user || !partners || !idFromUrl) {
      return;
    }

    const list = Object.values(partners);
    const found = list.find((p) => getPartnerFriendlyIdentifier(p) === idFromUrl);

    // unknown slug? default back to the user’s partner
    if (
      !found ||
      (found.partnerId === PLATFORM_PARTNER_ID && !hasPlatformPartnerAccess(groupRules))
    ) {
      handlePartnerSelect(user.partnerId);
      return;
    }

    // only set if the slug is changed
    if (idFromUrl !== idFromStore && !isNullish(found.partnerId)) {
      setSelectedPartnerId(found.partnerId);
    }
  }, [
    loading,
    user,
    partners,
    idFromUrl,
    idFromStore,
    groupRules,
    handlePartnerSelect,
    setSelectedPartnerId,
  ]);

  const currentlyReady = !loading && idFromUrl === idFromStore;

  // once currentlyReady flips to true, store that in state (so React will re-render)
  useEffect(() => {
    if (currentlyReady && !hasBeenReady) {
      ref.isReady = true;
      setHasBeenReady(true);
    }
  }, [currentlyReady, hasBeenReady]);

  return {
    // this will be false until “currentlyReady” is true, then stay true forever
    isReady: ref.isReady || hasBeenReady,
  };
};

export function getPartnerFriendlyIdentifier(p?: Partner | null): string {
  if (!p) return '';

  const slug = p[FRIENDLY_PARTNER_ID_FIELD];
  return slug || String(p.partnerId);
}

export function buildPartnerUrl({
  existingIdFromUrl = '',
  newPartner,
  pathname,
  search = '',
  hash = '',
}: {
  existingIdFromUrl?: string;
  newPartner?: Partner | null;
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const oldId = existingIdFromUrl;
  const newId = newPartner ? getPartnerFriendlyIdentifier(newPartner) : '';

  // split on '/', drop any empty segments
  const segments = pathname
    .split('/')
    .filter(Boolean)
    // if the first segment matches oldId, drop it
    .filter((seg, i) => !(i === 0 && seg === oldId));

  // if we have a newId, prepend it
  if (newId) segments.unshift(newId);

  const path = `/${segments.join('/')}`;
  return `${path}${search}${hash}`;
}
